import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../types';

// MED-1383: 'frontend' is a view switch with the admin's own tokens (partner
// contacts). 'backend' is a server-side impersonation with its own tokens.
// The banner and logout used to infer this by comparing refresh tokens, which
// breaks after any token rotation.
export type ImpersonationMode = 'frontend' | 'backend';

interface ImpersonationState {
  isImpersonating: boolean;
  mode: ImpersonationMode | null;
  // Original admin user data (preserved so we can restore)
  originalUser: User | null;
  originalAccessToken: string | null;
  originalRefreshToken: string | null;
  // Admin info for the banner
  adminName: string | null;
  adminEmail: string | null;
  // The impersonated user's display name and role
  impersonatedUserName: string | null;
  impersonatedUserRole: string | null;
}

interface ImpersonationActions {
  startImpersonation: (params: {
    mode: ImpersonationMode;
    originalUser: User;
    originalAccessToken: string;
    originalRefreshToken: string;
    adminName: string;
    adminEmail: string;
    impersonatedUserName: string;
    impersonatedUserRole: string;
  }) => void;
  endImpersonation: () => {
    originalUser: User;
    originalAccessToken: string;
    originalRefreshToken: string;
  } | null;
  clearImpersonation: () => void;
}

type ImpersonationStore = ImpersonationState & ImpersonationActions;

export const useImpersonationStore = create<ImpersonationStore>()(
  persist(
    (set, get) => ({
      // State
      isImpersonating: false,
      mode: null,
      originalUser: null,
      originalAccessToken: null,
      originalRefreshToken: null,
      adminName: null,
      adminEmail: null,
      impersonatedUserName: null,
      impersonatedUserRole: null,

      // Actions
      startImpersonation: (params) => {
        set({
          isImpersonating: true,
          mode: params.mode,
          originalUser: params.originalUser,
          originalAccessToken: params.originalAccessToken,
          originalRefreshToken: params.originalRefreshToken,
          adminName: params.adminName,
          adminEmail: params.adminEmail,
          impersonatedUserName: params.impersonatedUserName,
          impersonatedUserRole: params.impersonatedUserRole,
        });
      },

      endImpersonation: () => {
        const state = get();
        if (!state.isImpersonating || !state.originalUser) {
          return null;
        }

        const original = {
          originalUser: state.originalUser,
          originalAccessToken: state.originalAccessToken!,
          originalRefreshToken: state.originalRefreshToken!,
        };

        set({
          isImpersonating: false,
          mode: null,
          originalUser: null,
          originalAccessToken: null,
          originalRefreshToken: null,
          adminName: null,
          adminEmail: null,
          impersonatedUserName: null,
          impersonatedUserRole: null,
        });

        return original;
      },

      clearImpersonation: () => {
        set({
          isImpersonating: false,
          mode: null,
          originalUser: null,
          originalAccessToken: null,
          originalRefreshToken: null,
          adminName: null,
          adminEmail: null,
          impersonatedUserName: null,
          impersonatedUserRole: null,
        });
      },
    }),
    {
      name: 'impersonation-session',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
